import React from 'react';
import Container from './Container';
import Button from './Button';

/**
 * Page-level error boundary component
 * Catches errors that would otherwise crash the entire application
 */
class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error for debugging purposes
    console.error('Page Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // In production, you might want to log this to an error reporting service
    // Example: Sentry, LogRocket, or Cloudflare Analytics
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-brand-black text-white">
          <Container className="text-center max-w-2xl">
            <div className="mb-8">
              <svg className="w-24 h-24 mx-auto mb-6 text-brand-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Something went wrong</h1>
            <p className="text-lg md:text-xl text-gray-300 mb-8">
              We're sorry, but something unexpected happened. Our team has been notified and is working to fix the issue.
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button onClick={this.handleReload}>
                Reload Page
              </Button>
              <Button variant="secondary" onClick={this.handleGoHome}>
                Go to Homepage
              </Button>
            </div>

            {/* Show error details in development */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-8 text-left bg-red-900/20 border border-red-600 rounded-lg p-4">
                <summary className="cursor-pointer text-red-400 font-semibold mb-2">
                  Error Details (Development Only)
                </summary>
                <div className="text-sm text-red-300 font-mono">
                  <div className="mb-2">
                    <strong>Error:</strong> {this.state.error.toString()}
                  </div>
                  <div>
                    <strong>Stack Trace:</strong>
                    <pre className="mt-1 text-xs overflow-auto">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </div>
                </div>
              </details>
            )}
          </Container>
        </div>
      );
    }

    return this.props.children;
  }
}

export default PageErrorBoundary;
